/**
 * Permission Constants
 * These match the exact permissions returned from /api/roles
 * Using constants prevents typos and makes refactoring easier
 */

import { ROLES } from './roles';

export const PERMISSIONS = {
  // Leads
  LEADS_LIST: 'leads:list',
  LEADS_READ: 'leads:read',
  LEADS_CREATE: 'leads:create',
  LEADS_UPDATE: 'leads:update',
  LEADS_DELETE: 'leads:delete',
  LEADS_ASSIGN_TAGS: 'leads:assign_tags',

  // Lead Notes
  LEAD_NOTES_LIST: 'lead_notes:list',
  LEAD_NOTES_CREATE: 'lead_notes:create',

  // Lead Transcripts
  LEAD_TRANSCRIPTS_READ: 'lead_transcripts:read',

  // Users
  USERS_LIST: 'users:list',
  USERS_READ: 'users:read',
  USERS_CREATE: 'users:create',
  USERS_UPDATE: 'users:update',
  USERS_DELETE: 'users:delete',
  USERS_ASSIGN_ROLE: 'users:assign_role',

  // Tags
  TAGS_LIST: 'tags:list',
  TAGS_CREATE: 'tags:create',
  TAGS_UPDATE: 'tags:update',
  TAGS_DELETE: 'tags:delete',

  // Customer Settings
  CUSTOMER_READ_SETTINGS: 'customer:read_settings',
  CUSTOMER_UPDATE_SETTINGS: 'customer:update_settings',

  // System
  SYSTEM_MANAGE_ROLES: 'system:manage_roles',
  SYSTEM_MANAGE_ENTITLEMENTS: 'system:manage_entitlements',

  // Families
  FAMILIES_LIST: 'families:list',
  FAMILIES_READ: 'families:read',
  FAMILIES_CREATE: 'families:create',
  FAMILIES_UPDATE: 'families:update',
  FAMILIES_DELETE: 'families:delete',
  FAMILIES_MANAGE_MEMBERS: 'families:manage_members',

  // Courses
  COURSES_LIST: 'courses:list',
  COURSES_CREATE: 'courses:create',
  COURSES_UPDATE: 'courses:update',
  COURSES_DELETE: 'courses:delete',
  COURSES_MANAGE: 'courses:manage',

  // Classes
  CLASSES_LIST: 'classes:list',
  CLASSES_READ: 'classes:read',
  CLASSES_CREATE: 'classes:create',
  CLASSES_UPDATE: 'classes:update',
  CLASSES_DELETE: 'classes:delete',
  CLASSES_MANAGE: 'classes:manage',

  // Tutors
  TUTORS_LIST: 'tutors:list',
  TUTORS_READ: 'tutors:read',
  TUTORS_MANAGE: 'tutors:manage',

  // Events
  EVENTS_LIST: 'events:list',
  EVENTS_CREATE: 'events:create',
  EVENTS_UPDATE: 'events:update',
  EVENTS_DELETE: 'events:delete',

  // Event Enrollments
  EVENT_ENROLLMENTS_MANAGE: 'event_enrollments:manage',

  // Event Attendance
  EVENT_ATTENDANCE_MANAGE: 'event_attendance:manage',

  // Enrollments
  ENROLLMENTS_MANAGE: 'enrollments:manage',

  // Attendance
  ATTENDANCE_TAKE: 'attendance:take',
  ATTENDANCE_READ: 'attendance:read',
  ATTENDANCE_READ_OWN: 'attendance:read_own',

  // Notes
  NOTES_CREATE: 'notes:create',
  NOTES_LIST: 'notes:list',
  NOTES_READ_OWN: 'notes:read_own',
  NOTES_UPDATE: 'notes:update',
  NOTES_DELETE: 'notes:delete',

  // Stages
  STAGES_READ: 'stages:read',
  STAGES_MANAGE: 'stages:manage',

  // Custom Fields
  CUSTOM_FIELDS_READ: 'custom_fields:read',
  CUSTOM_FIELDS_MANAGE: 'custom_fields:manage',

  // Actions
  ACTIONS_READ: 'actions:read',
  ACTIONS_MANAGE: 'actions:manage',
  STAGES_LINK_ACTIONS: 'stages:link_actions',

  // Students
  STUDENT_CREATE: 'student:create',
  STUDENT_UPDATE: 'student:update',
  STUDENT_DELETE: 'student:delete',
  STUDENT_LIST: 'student:list',
  STUDENT_READ: 'student:read',

  // Roles
  ROLES_READ: 'roles:read',
};

/**
 * Route Permission Map
 * Define which permissions are required to access each route
 * Use arrays for "any of these" (OR logic)
 */
export const ROUTE_PERMISSIONS = {
  '/': [PERMISSIONS.LEADS_LIST],
  '/families': [PERMISSIONS.FAMILIES_LIST],
  '/student-enrollment': [PERMISSIONS.STUDENT_LIST, PERMISSIONS.ENROLLMENTS_MANAGE],
  '/class-scheduling': [PERMISSIONS.CLASSES_LIST],
  '/class-management': [PERMISSIONS.CLASSES_LIST],
  '/student-checkin': [PERMISSIONS.ATTENDANCE_TAKE],
  '/scheduling-manager': [PERMISSIONS.ATTENDANCE_READ, PERMISSIONS.ATTENDANCE_TAKE],
  '/tutor-notes': [PERMISSIONS.NOTES_LIST],
  '/administration': [PERMISSIONS.SYSTEM_MANAGE_ROLES, PERMISSIONS.USERS_LIST],
  '/settings': [PERMISSIONS.CUSTOMER_READ_SETTINGS],
  '/reports': [PERMISSIONS.CLASSES_LIST], // Adjust based on your needs
};

/**
 * Role-based Feature Access
 * Maps roles to features they can access
 * Use this for easier role-based access control instead of checking individual permissions
 */
export const ROLE_FEATURES = {
  // Lead Page Features by Role
  LEAD_PAGE: {
    CREATE_LEAD: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
    EDIT_LEAD: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
    DELETE_LEAD: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN],
    ADD_NOTE: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR, ROLES.TUTOR],
    VIEW_TRANSCRIPT: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
    MANAGE_TAGS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
  },

  // Family Management Features by Role
  FAMILY_PAGE: {
    CREATE_FAMILY: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
    EDIT_FAMILY: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
    DELETE_FAMILY: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN],
    ADD_MEMBER: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
    REMOVE_MEMBER: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN],
    CREATE_STUDENT: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
  },

  // Class Scheduling Features by Role
  CLASS_PAGE: {
    CREATE_CLASS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
    EDIT_CLASS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
    DELETE_CLASS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN],
    CREATE_EVENT: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR, ROLES.TUTOR],
    MANAGE_ENROLLMENTS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
  },

  // Attendance Features by Role
  ATTENDANCE_PAGE: {
    TAKE_ATTENDANCE: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR, ROLES.TUTOR],
    EDIT_ATTENDANCE: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR,ROLES.TUTOR],
    VIEW_ALL_ATTENDANCE: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR,ROLES.TUTOR],
  },

  // Administration Features by Role
  ADMIN_PAGE: {
    MANAGE_USERS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN],
    DELETE_USERS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN],
    ASSIGN_ROLES: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN],
    MANAGE_ROLES: [ROLES.SYSTEM_ADMIN],
    MANAGE_TAGS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
  },

  // Settings Features by Role
  SETTINGS_PAGE: {
    VIEW_SETTINGS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
    EDIT_SETTINGS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN],
    MANAGE_CUSTOM_FIELDS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN],
    MANAGE_STAGES: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
    MANAGE_TAGS: [ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN, ROLES.CENTER_DIRECTOR],
  },
};

/**
 * Page Feature Map
 * Define which features should be enabled on each page based on permissions
 * LEGACY: Consider using ROLE_FEATURES instead for easier management
 */
export const PAGE_FEATURES = {
  // Lead Page Features
  LEAD_PAGE: {
    CREATE_LEAD: [PERMISSIONS.LEADS_CREATE],
    EDIT_LEAD: [PERMISSIONS.LEADS_UPDATE],
    DELETE_LEAD: [PERMISSIONS.LEADS_DELETE],
    ADD_NOTE: [PERMISSIONS.LEAD_NOTES_CREATE],
    VIEW_TRANSCRIPT: [PERMISSIONS.LEAD_TRANSCRIPTS_READ],
    MANAGE_TAGS: [PERMISSIONS.LEADS_ASSIGN_TAGS],
  },

  // Family Management Features
  FAMILY_PAGE: {
    CREATE_FAMILY: [PERMISSIONS.FAMILIES_CREATE],
    EDIT_FAMILY: [PERMISSIONS.FAMILIES_UPDATE],
    DELETE_FAMILY: [PERMISSIONS.FAMILIES_DELETE],
    ADD_MEMBER: [PERMISSIONS.FAMILIES_MANAGE_MEMBERS],
    REMOVE_MEMBER: [PERMISSIONS.FAMILIES_MANAGE_MEMBERS],
    CREATE_STUDENT: [PERMISSIONS.STUDENT_CREATE],
  },

  // Class Scheduling Features
  CLASS_PAGE: {
    CREATE_CLASS: [PERMISSIONS.CLASSES_CREATE],
    EDIT_CLASS: [PERMISSIONS.CLASSES_UPDATE],
    DELETE_CLASS: [PERMISSIONS.CLASSES_DELETE],
    CREATE_EVENT: [PERMISSIONS.EVENTS_CREATE],
    MANAGE_ENROLLMENTS: [PERMISSIONS.EVENT_ENROLLMENTS_MANAGE],
  },

  // Attendance Features
  ATTENDANCE_PAGE: {
    TAKE_ATTENDANCE: [PERMISSIONS.ATTENDANCE_TAKE],
    EDIT_ATTENDANCE: [PERMISSIONS.EVENT_ATTENDANCE_MANAGE],
    VIEW_ALL_ATTENDANCE: [PERMISSIONS.ATTENDANCE_READ],
  },

  // Administration Features
  ADMIN_PAGE: {
    MANAGE_USERS: [PERMISSIONS.USERS_CREATE, PERMISSIONS.USERS_UPDATE],
    DELETE_USERS: [PERMISSIONS.USERS_DELETE],
    ASSIGN_ROLES: [PERMISSIONS.USERS_ASSIGN_ROLE],
    MANAGE_ROLES: [PERMISSIONS.SYSTEM_MANAGE_ROLES],
    MANAGE_TAGS: [PERMISSIONS.TAGS_CREATE, PERMISSIONS.TAGS_UPDATE, PERMISSIONS.TAGS_DELETE],
  },

  // Settings Features
  SETTINGS_PAGE: {
    VIEW_SETTINGS: [PERMISSIONS.CUSTOMER_READ_SETTINGS],
    EDIT_SETTINGS: [PERMISSIONS.CUSTOMER_UPDATE_SETTINGS],
    MANAGE_CUSTOM_FIELDS: [PERMISSIONS.CUSTOM_FIELDS_MANAGE],
    MANAGE_STAGES: [PERMISSIONS.STAGES_MANAGE],
    MANAGE_TAGS: [PERMISSIONS.TAGS_CREATE, PERMISSIONS.TAGS_UPDATE, PERMISSIONS.TAGS_DELETE],
  },
};
